'use client';

/**
 * AskComposeSurface — the compositor-backed Ask Klyde rail surface.
 *
 * Parallel to `AskSurface` (which stays on `docs_ask`). This one calls the
 * `ask` compositor (klyde_ask) and renders the ComposedAnswer's grouped lanes:
 *   - knowledge → docs prose,
 *   - capability → ki_ask result / clarification,
 *   - navigate → a "Go to …" button (the coordinate, consumed here: the
 *     compositor produced it, this surface acts on it — change mode + select
 *     the entity).
 *
 * Grouped, never blended; the navigate card is inert until clicked (a
 * reference is an affordance, not an act). Ships beside the docs surface so
 * the working path is untouched — A/B by surface.
 *
 * @component_role: Card Surface
 * @component_type: Layout Peer
 * @domain: Frontend Shell / Agent
 * @layer: presentation
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Send, Sparkle, RotateCcw, ArrowRight, Maximize2, Minimize2, ChevronRight } from 'lucide-react';
import { api } from '@/lib/api/client';
import { useAskComposeStore, type AskComposeTurn } from '@/lib/stores/ask-compose-store';
import { useAgentCanvasStore } from '@/lib/stores/agent-canvas-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useTabStore } from '@/lib/stores/tab-store';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useAskLabel } from '@/lib/stores/total-matrix-store';
import type { ComposedAnswer, AskView, NavigateView } from '@/lib/api/ask-types';
import { surfaceBase, text as tokenText } from '@/lib/design/tokens';
import { useCardExport } from '@/lib/design/card-export';
import { cn } from '@/lib/utils/cn';

type AskReq = { query: string; context?: string; knowledge_mode?: string; conversation_id?: string; lanes?: string[]; sa_defer?: boolean; sa_key?: string };

async function askCompose(params: AskReq): Promise<ComposedAnswer> {
  const res = await api.getStrategyData<ComposedAnswer>('ask', params);
  if (!res.success) throw new Error(res.error || 'ask failed');
  return res.data;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);

/** One-line summary for a collapsed tier — whitespace collapsed so CSS `truncate`
 *  shows a clean single line rather than the first physical line of a paragraph. */
const collapse = (s: string): string => s.replace(/\s+/g, ' ').trim();

/** The knowledge lane carries the docs-tier signals: `llm_status` (whether a
 *  deeper synthesis is worth fetching) and the docs `conversation_id` to
 *  thread the full pass onto. Mirrors the rail's two-tier handshake. */
function knowledgeSignals(answer: ComposedAnswer): { llmStatus: string | null; conversationId: string | null; saKey: string | null } {
  const v = answer.view;
  // saKey — present when slot 5 was DEFERRED: fire a second, slot-5-only call with it to
  // resolve the parked record and upgrade the turn in place with the "What to watch" aside.
  // conversation_id is NOT carried by the clean view (multi-turn threading is a separate,
  // currently-inert concern — the shim never emitted it either), so it stays null.
  return { llmStatus: str(v?.llm_status), conversationId: null, saKey: str(v?.sa_key) };
}

export function AskComposeSurface() {
  const turns = useAskComposeStore((s) => s.turns);
  const isLoading = useAskComposeStore((s) => s.isLoading);
  const setAnswer = useAskComposeStore((s) => s.setAnswer);
  const upgradeLatestTurn = useAskComposeStore((s) => s.upgradeLatestTurn);
  const setLoading = useAskComposeStore((s) => s.setLoading);
  const setLastQuery = useAskComposeStore((s) => s.setLastQuery);
  const clear = useAskComposeStore((s) => s.clear);

  const [query, setQuery] = useState('');
  // Pattern A chrome — every string from ui_config/labels/ask.py.
  const L = {
    title: useAskLabel('title'),
    subtitle: useAskLabel('subtitle_composed'),
    newLabel: useAskLabel('new_conversation'),
    newTitle: useAskLabel('new_conversation_title'),
    emptyHint: useAskLabel('empty_hint'),
    placeholder: useAskLabel('input_placeholder'),
    send: useAskLabel('send'),
  };
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastQueryRef = useRef<string | null>(null);
  // The docs conversation_id, PERSISTED across turns. docs_ask threads history
  // server-side by this id (get_or_create_context → get_history_prompt + the
  // condense step that rewrites a follow-up against prior turns). Sending it on
  // every turn is what makes the conversation multi-turn; without it each query
  // opened a fresh conversation and re-answered from scratch.
  const conversationIdRef = useRef<string | null>(null);

  // Slot 5 (deferred) — the second, slot-5-only call. Resolves the parked record for
  // `sa_key` and returns the FULL turn (slots 1-4 + the "What to watch" aside), swapped in
  // via upgradeLatestTurn. Runs OFF the answer's clock — the answer already painted, this
  // just appends the aside a beat later. A cache miss returns an empty answer → skip the
  // upgrade so the turn's existing slots survive.
  const askSa = useMutation<ComposedAnswer, Error, { sa_key: string }>({
    mutationFn: ({ sa_key }) => askCompose({ query: '', sa_key }),
    onSuccess: (answer) => {
      if (answer.view) upgradeLatestTurn(answer);
    },
    onError: (e) => {
      // Additive lane — a failed SA resolve never touches the answer already shown.
      console.warn('[ask-compose] slot-5 resolve failed (aside skipped):', e);
    },
  });

  // Full synthesis (knowledge_mode='full'). Two callers:
  //  - turn 1: fired behind the fast preview to UPGRADE it in place.
  //  - follow-ups: fired directly as a NEW turn — follow-ups skip the fast tier
  //    (see submit). `newTurn` picks append vs upgrade-latest.
  const askDeep = useMutation<ComposedAnswer, Error, AskReq & { newTurn?: boolean }>({
    mutationFn: ({ newTurn: _newTurn, ...req }) => askCompose(req),
    onSuccess: (answer, vars) => {
      const { conversationId, saKey } = knowledgeSignals(answer);
      if (conversationId) conversationIdRef.current = conversationId;
      if (vars.newTurn) {
        setAnswer(answer);
        setQuery('');
      } else {
        upgradeLatestTurn(answer);
      }
      // Slot 5 was deferred — resolve it behind the painted answer.
      if (saKey) askSa.mutate({ sa_key: saKey });
    },
    onError: (e) => {
      // Don't swallow — a failing tier silently degrades the answer to just
      // the fast Knowledge lane (no richer lanes), which reads as a regression.
      console.error('[ask-compose] tier failed — richer lanes skipped:', e);
      setLoading(false);
    },
  });

  // Phase 1 — fast tier (knowledge_mode='auto'): the card + curated answer in
  // ~1.8s, no LLM. If the knowledge lane signals 'auto_running', fire the
  // deeper synthesis behind it (threaded to the same docs conversation).
  const askFast = useMutation<ComposedAnswer, Error, AskReq>({
    mutationFn: askCompose,
    onSuccess: (answer) => {
      setAnswer(answer);
      setQuery('');
      const { llmStatus, conversationId, saKey } = knowledgeSignals(answer);
      // Capture the docs conversation_id so the NEXT turn threads to it.
      if (conversationId) conversationIdRef.current = conversationId;
      if (llmStatus === 'auto_running') {
        askDeep.mutate({
          query: lastQueryRef.current ?? '',
          knowledge_mode: 'full',
          conversation_id: conversationIdRef.current ?? undefined,
          sa_defer: true,
        });
      }
      // Slot 5 was deferred — resolve it behind the painted answer.
      if (saKey) askSa.mutate({ sa_key: saKey });
    },
    onError: (e) => {
      // Don't swallow — a failing tier silently degrades the answer to just
      // the fast Knowledge lane (no richer lanes), which reads as a regression.
      console.error('[ask-compose] tier failed — richer lanes skipped:', e);
      setLoading(false);
    },
  });

  // Inverted feed: the newest turn lands at the TOP, under the composer — reveal it.
  // On tier UPGRADES (turns.length unchanged) we deliberately do NOT scroll: the
  // scroll container's `overflow-anchor` holds the reader's block still while a newer
  // tier inserts above it (handover §2, "no scroll jump").
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [turns.length, isLoading]);

  const submit = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isLoading) return;
    lastQueryRef.current = trimmed;
    setLastQuery(trimmed);
    setLoading(true);
    if (conversationIdRef.current) {
      // FOLLOW-UP — skip the fast preview. The fast tier is the cold-open
      // affordance (turn 1: a curated card in ~2s); a follow-up's value is
      // synthesis WITH the prior turns' context, and the cached single-hit
      // preview is exactly what surfaced an unrelated card. Straight to full
      // synthesis, threaded to the conversation, as a NEW turn.
      askDeep.mutate({
        query: trimmed,
        knowledge_mode: 'full',
        conversation_id: conversationIdRef.current,
        newTurn: true,
        sa_defer: true,
      });
    } else {
      // TURN 1 — fast pass = the KNOWLEDGE lane alone (~2.1s), where the card
      // is. The compositor is a barrier, so a full fan-out would wait on the
      // slow capability arm (ki_ask OODA); scoping to knowledge skips it. The
      // fast tier's onSuccess fires the full synthesis behind it to upgrade.
      askFast.mutate({ query: trimmed, knowledge_mode: 'auto', lanes: ['knowledge'], sa_defer: true });
    }
  };

  return (
    <div className="flex flex-col h-full bg-background border-l border-border" style={{ fontSize: surfaceBase.rail }}>
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/10 text-primary">
          <Sparkle className="h-3.5 w-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className={cn(tokenText.title, 'font-semibold leading-tight')}>{L.title}</div>
          <div className={cn(tokenText.muted, 'leading-tight')}>{L.subtitle}</div>
        </div>
        {turns.length > 0 && (
          <button
            onClick={() => {
              conversationIdRef.current = null;
              clear();
            }}
            className={cn(tokenText.muted, 'flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-muted hover:text-foreground transition-colors')}
            title={L.newTitle}
          >
            <RotateCcw className="h-3 w-3" />
            {L.newLabel}
          </button>
        )}
      </div>

      {/* Composer docks at the TOP (handover §5) — new answers insert directly beneath
          it (inverted feed). The field is a soft pill, not a flush hard-edged box:
          rounded, muted fill, focus lifts the ring — an inviting input, not an IT
          form field. */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit(query);
        }}
        className="border-b border-border/60 px-2.5 py-2"
      >
        <div className="flex items-center gap-1.5 rounded-2xl bg-muted/50 px-3 py-1.5 ring-1 ring-transparent transition-colors focus-within:bg-muted/70 focus-within:ring-primary/30">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={L.placeholder}
            disabled={isLoading}
            className={cn(tokenText.body, 'flex-1 bg-transparent outline-none placeholder:text-muted-foreground disabled:opacity-50')}
            aria-label={L.title}
          />
          <button
            type="submit"
            disabled={!query.trim() || isLoading}
            className={cn(
              'rounded-full p-1 text-muted-foreground transition-colors',
              query.trim() && !isLoading && 'text-primary hover:bg-primary/10',
              (!query.trim() || isLoading) && 'opacity-40',
            )}
            aria-label={L.send}
          >
            {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          </button>
        </div>
      </form>

      {/* `overflow-anchor` holds the block the reader is on when a newer tier inserts
          above it — the "no scroll jump" requirement, done natively (handover §2). */}
      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3"
        style={{ overflowAnchor: 'auto' }}
      >
        {isLoading && <ComposingSkeleton />}
        {!isLoading && askDeep.isPending && <DeepeningHint />}
        {turns.length === 0 && !isLoading && (
          <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground py-8">
            <Sparkle className="h-6 w-6 text-primary/60 mb-2" />
            <div className={cn(tokenText.body, 'max-w-[220px] leading-relaxed')}>
              {L.emptyHint}
            </div>
          </div>
        )}
        {/* Inverted feed (handover §2): newest turn directly under the composer,
            older turns pushed down. Keyed by timestamp so a reversed render never
            remounts a turn (which would drop its accordion state). */}
        {[...turns].reverse().map((turn) => (
          <ComposeTurn key={turn.timestamp} turn={turn} />
        ))}
      </div>
    </div>
  );
}

function ComposeTurn({ turn }: { turn: AskComposeTurn }) {
  // The FLIP, conversation-anchored. Slot frame: the question + the synthesis
  // (the Q&A text) are the anchor and NEVER leave the rail; the answer's CARDS
  // (slot 3 + reserved) are what move to the wide overlay and back. Placement
  // is by slot, not CardSize — every card renders in both; the user flips for
  // room. `inCanvas` derives from the overlay store (this turn is the one
  // flipped in), so the cards render in EXACTLY one place — no duplication.
  const detent = useAgentCanvasStore((s) => s.detent);
  const composeKey = useAgentCanvasStore((s) => s.composeKey);
  const openCompose = useAgentCanvasStore((s) => s.openCompose);
  const close = useAgentCanvasStore((s) => s.close);
  const turnKey = `t${turn.timestamp}`;
  const inCanvas = detent !== 'closed' && composeKey === turnKey;
  const view = turn.answer.view ?? null;
  // The card count for this turn, straight off the clean view — the proposal GROUP
  // (view.cards, e.g. the whole composer) or the single dispatched card (view.card). The
  // overlay reads the view, so we hand it the WHOLE answer (facets are empty on the
  // grammar path); no per-facet filtering here anymore.
  const cardCount = view?.cards?.length ?? (view?.card?.renderer_card ? 1 : 0);

  // Auto-open the canvas overlay when a dispatched card first arrives — the answer's
  // live surface shouldn't wait for a click. Card-gated: a bare knowledge turn never
  // opens the canvas. Fires ONCE per turn (ref-guarded) so bringing it back doesn't refire.
  const hasCard = cardCount > 0;
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (hasCard && !autoOpenedRef.current) {
      autoOpenedRef.current = true;
      openCompose(turn.answer, turnKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCard]);

  // Keep the FLIPPED copy in sync with the turn. The deep tier upgrades
  // the rail turn in place (upgradeLatestTurn) — without this, a flip
  // made during the fast tier leaves the canvas store holding the fast
  // answer while the rail shows the upgraded one (the overlay's quick
  // answer / charts would be the stale snapshot).
  useEffect(() => {
    if (!inCanvas) return;
    openCompose(turn.answer, turnKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inCanvas, turn.answer]);

  // ── Scene-capture registration ─────────────────────────────────────
  // Ask lives in zustand + useMutation, so it's invisible to both halves of
  // the capture envelope by default: no useCardExport → absent from
  // scene.surfaces; mutation results never enter the query cache → nothing
  // for the payload derivation to find. Join the covenant the same way every
  // other card does, with two moves:
  //  1. Mirror the ComposedAnswer into the query cache under a turn-scoped
  //     key (the store stays the render source — the cache copy is the
  //     capturable address for "what the BFF said, verbatim").
  //  2. Subscribe via a disabled useQuery — getObservedPayloads skips
  //     entries with zero observers, and a disabled query registers an
  //     observer without ever fetching (gcTime: Infinity so the entry
  //     outlives the default GC while the turn is mounted).
  const askQueryKey = ['ask-compose', turnKey];
  const queryClient = useQueryClient();
  useEffect(() => {
    queryClient.setQueryData(askQueryKey, turn.answer);
    // Re-runs when the deep tier upgrades the turn in place.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, turnKey, turn.answer]);
  useQuery({
    queryKey: askQueryKey,
    queryFn: () => Promise.reject(new Error('capture-only key — fed by setQueryData')),
    enabled: false,
    gcTime: Infinity,
    staleTime: Infinity,
  });
  // Render + capture read the CLEAN view (defined at the top of the turn). Slots:
  // 1+2 = view.quick, 3 = view.synthesis, 5 = view.sa_read.
  const answerText = str(view?.synthesis) ?? str(view?.quick?.short) ?? '';
  const turnLabel = useAskLabel('title');
  const noLanesLabel = useAskLabel('no_lanes');
  useCardExport({
    label: turnLabel,
    content_key: 'ask_compose_turn',
    surface: 'rail',
    slot_id: `ask:${turnKey}`,
    text: `Q: ${turn.query}\nA: ${answerText}`,
    json: {
      query: turn.query,
      lanes: turn.answer.facets.map((f) => f.lane),   // capture metadata (facets still shipped)
      fast_short: str(view?.quick?.short),
      fast_long: str(view?.quick?.long),
      answer: answerText,
      synthesis: str(view?.synthesis) ?? null,
      // slot 5 — the situational-awareness read + the record SIDs it drew on
      sa_read: str(view?.sa_read) ?? null,
      sa_refs: view?.sa_refs ?? null,
    },
    state: { in_canvas: inCanvas },
    status: 'loaded',
    query_key: askQueryKey,
  });

  return (
    <div className="space-y-2">
      <div className="flex justify-end">
        <div className={cn(tokenText.body, 'max-w-[85%] rounded-lg rounded-tr-sm bg-primary/15 px-2.5 py-1.5')}>{turn.query}</div>
      </div>
      {!view && (
        <LaneBlock>
          <div className={cn(tokenText.muted)}>{noLanesLabel}</div>
        </LaneBlock>
      )}
      {view && <KnowledgeLane view={view} />}
      {view?.navigate?.entity_sid && <NavigateLane nav={view.navigate} />}
      {cardCount > 0 && (
        <FlipControl
          inCanvas={inCanvas}
          count={cardCount}
          onFlip={() => openCompose(turn.answer, turnKey)}
          onReturn={close}
        />
      )}
    </div>
  );
}

/** Turn-level flip — the answer's CARDS move to the wide overlay (the
 *  conversation stays in the rail), or come back. One control per turn, not
 *  per-lane: the cards travel together. */
function FlipControl({
  inCanvas,
  count,
  onFlip,
  onReturn,
}: {
  inCanvas: boolean;
  count: number;
  onFlip: () => void;
  onReturn: () => void;
}) {
  const nounOne = useAskLabel('noun_card');
  const nounMany = useAskLabel('noun_cards', { n: count });
  const noun = count > 1 ? nounMany : nounOne;
  const bringBack = useAskLabel('flip_bring_back', { noun });
  const viewInCanvas = useAskLabel('flip_view_in_canvas', { noun });
  const returnTitle = useAskLabel('flip_return_title');
  const openTitle = useAskLabel('flip_open_title');
  return inCanvas ? (
    <button
      onClick={onReturn}
      className={cn(tokenText.muted, 'ml-7 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-primary hover:bg-primary/10 transition-colors')}
      title={returnTitle}
    >
      <Minimize2 className="h-3 w-3" />
      {bringBack}
    </button>
  ) : (
    <button
      onClick={onFlip}
      className={cn(tokenText.muted, 'ml-7 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-primary hover:bg-primary/10 transition-colors')}
      title={openTitle}
    >
      <Maximize2 className="h-3 w-3" />
      {viewInCanvas}
    </button>
  );
}

function KnowledgeLane({ view }: { view: AskView }) {
  const llmStatus = str(view.llm_status);
  const synthesis = llmStatus === 'completed' ? str(view.synthesis) : null;
  // Slot 5 — the situational-awareness aside, present only on a dispatch turn.
  const saRead = str(view.sa_read);
  // Slots 1+2 — the grounded doc's OWN quick answer (view.quick), DISTINCT from slot 3
  // (synthesis) and slot 4 (the card): the doc's words and Klyde's answer both show.
  const short = str(view.quick?.short);
  let long = str(view.quick?.long);
  if (long && short && long === short) long = null; // degenerate (short === long)
  // A card this turn (the flipped surface lives on the canvas) keeps a card-only turn
  // from reading as empty here — singular `card` or the proposal `cards` group.
  const hasCard = !!(view.card?.renderer_card || (view.cards && view.cards.length));

  const quickLabel = useAskLabel('quick_answer');
  const answerLabel = useAskLabel('answer');
  const watchLabel = useAskLabel('situational_awareness');
  const reservedLabel = useAskLabel('quick_answer_reserved');
  const emptyLabel = useAskLabel('knowledge_empty');

  // Disclosure (handover §2). The tiers are ONE card kind in a shared shell. The
  // first tier (quick answer) auto-expands; every later tier ARRIVES COLLAPSED and
  // never opens itself. Tiers toggle INDEPENDENTLY — the reader can keep any or all
  // open — and only a USER tap changes a tier's state, never a newer tier landing.
  // So nothing the reader is engaged with vanishes under them, and the answer keeps
  // its rank over the addendum (rank isn't decided by arrival timing).
  const [open, setOpen] = useState<Set<'quick' | 'answer' | 'watch'>>(() => new Set(['quick']));
  const toggle = (t: 'quick' | 'answer' | 'watch') =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });

  if (!synthesis && !short && !hasCard && !saRead) {
    return <div className={cn(tokenText.muted)}>{emptyLabel}</div>;
  }

  // Peak-first: newest tier on top — what-to-watch (slot 5) · answer (slot 3) ·
  // quick answer (slots 1+2). Every block is the SAME shell; identity is the eyebrow.
  return (
    <div className="space-y-1.5">
      {saRead && (
        <StackCard
          eyebrow={watchLabel}
          summary={collapse(saRead)}
          expanded={open.has('watch')}
          onToggle={() => toggle('watch')}
        >
          <TierBody body={saRead} />
        </StackCard>
      )}
      {synthesis && (
        <StackCard
          eyebrow={answerLabel}
          summary={collapse(synthesis)}
          expanded={open.has('answer')}
          onToggle={() => toggle('answer')}
        >
          <TierBody body={synthesis} />
        </StackCard>
      )}
      <StackCard
        eyebrow={quickLabel}
        summary={short ? collapse(short) : reservedLabel}
        expanded={open.has('quick')}
        onToggle={() => toggle('quick')}
      >
        <QuickBody short={short} long={long} />
      </StackCard>
    </div>
  );
}

/** One card kind, one shell (handover §3): `bg-card`, 1px border, `rounded-md`, no
 *  elevation, no left accent — flat rows in a rail. Collapsed = chevron + eyebrow +
 *  one-line summary; expanded = eyebrow + body. `aria-expanded` conveys the state and
 *  the eyebrow is the button's accessible name, so no extra chrome string is needed. */
function StackCard({
  eyebrow,
  summary,
  expanded,
  onToggle,
  children,
}: {
  eyebrow: string;
  summary: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-md border border-border bg-card">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full items-center gap-1.5 px-2.5 py-1.5 text-left"
      >
        <ChevronRight
          className={cn('h-3 w-3 shrink-0 text-muted-foreground transition-transform', expanded && 'rotate-90')}
        />
        <span className={cn(tokenText.eyebrow, 'shrink-0')}>{eyebrow}</span>
        {!expanded && (
          <span className={cn(tokenText.muted, 'min-w-0 flex-1 truncate')}>{summary}</span>
        )}
      </button>
      {expanded && <div className="px-2.5 pb-2">{children}</div>}
    </div>
  );
}

/** Every tier's body renders identically (handover §4): one token (`text.body`),
 *  one leading, and NO inner box — the StackCard is the container. This is the fix
 *  for the three-costume drift (one foreground `text.body` vs two muted `ProseBlock`s,
 *  the latter also double-boxed). `ProseBlock` is kept for the overlay / other lanes,
 *  which own their own container; the rail tier stack must not nest one. */
function TierBody({ body }: { body: string }) {
  return <div className={cn(tokenText.body, 'leading-relaxed whitespace-pre-wrap')}>{body}</div>;
}

/** Slots 1+2 body — the quick answer (short) with the deeper reading behind a
 *  more/less toggle. Reserved-placeholder when no short landed, so the slot's
 *  absence is diagnosable rather than silently missing. */
function QuickBody({ short, long }: { short: string | null; long: string | null }) {
  const [more, setMore] = useState(false);
  const moreLabel = useAskLabel('more');
  const lessLabel = useAskLabel('less');
  const reservedLabel = useAskLabel('quick_answer_reserved');
  if (!short) {
    return <div className={cn(tokenText.muted, 'italic opacity-70')}>{reservedLabel}</div>;
  }
  return (
    <>
      <TierBody body={short} />
      {long && (
        <button
          type="button"
          onClick={() => setMore((v) => !v)}
          className={cn(tokenText.muted, 'mt-1 text-primary hover:underline')}
        >
          {more ? lessLabel : moreLabel}
        </button>
      )}
      {long && more && (
        <div className={cn(tokenText.muted, 'mt-1 leading-relaxed whitespace-pre-wrap')}>{long}</div>
      )}
    </>
  );
}

function NavigateLane({ nav }: { nav: NavigateView }) {
  const openTab = useTabStore((s) => s.openTab);
  const setExplorerSelection = useLayoutStore((s) => s.setExplorerSelection);
  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const hint = str(nav.hint) ?? str(nav.entity_sid) ?? '';
  const etype = str(nav.entity_type) ?? 'entity';
  const goToLabel = useAskLabel('go_to', { name: hint });
  if (!nav.entity_sid) return null;

  const go = () => {
    // The shim derived coordinate.selection = entity_type || 'entity'; reproduce it.
    const sel = str(nav.entity_type) ?? etype;
    // Full context switch: openTab (mode-promote + tab the canvas renders),
    // setExplorerSelection (force the canvas's strategySelection past the home
    // value — explorerSelection wins over tab.type), selectNode (rail).
    openTab({ id: `${sel}-${nav.entity_sid}`, type: sel, title: hint, entityId: nav.entity_sid });
    setExplorerSelection(sel);
    // 2nd arg = the render-context project (the canvas's project cards need it).
    selectNode({ id: nav.entity_sid, label: hint, type: etype }, nav.project_sid ?? undefined);
  };

  return (
    <div className="flex justify-start pl-7">
      <button
        onClick={go}
        className={cn(tokenText.body, 'inline-flex items-center gap-1.5 rounded-md border border-primary/30 bg-primary/5 px-2.5 py-1.5 text-primary hover:bg-primary/10 transition-colors')}
        title={goToLabel}
      >
        <ArrowRight className="h-3.5 w-3.5" />
        {goToLabel}
      </button>
    </div>
  );
}

/** In-flight compose state — a sized, lane-shaped skeleton in place of a
 *  spinner (the skeleton-cover mechanic from the answer-choreography design
 *  pass). It occupies roughly the footprint the knowledge lane will land in,
 *  so the real answer swaps in without a layout jump and the wait reads as
 *  "composing", not "hung".
 *
 *  This is a shape PRIOR — most answers lead with a knowledge lane — NOT the
 *  server's DECIDE plan. True per-lane skeletons driven off the actual plan
 *  need progressive (streaming / two-phase) transport, where the plan can
 *  reach the client before the facets do; that's a later slice. Bars use
 *  `bg-muted` (no invented colour) and honour reduced-motion. */
function ComposingSkeleton() {
  const composingLabel = useAskLabel('composing');
  const composingSr = useAskLabel('composing_sr');
  return (
    <div className="flex justify-start gap-1.5" role="status" aria-label={composingLabel}>
      <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-primary/10 text-primary">
        <Sparkle className="h-3 w-3" />
      </div>
      <div className="flex-1 max-w-[90%] rounded-lg rounded-tl-sm border border-border bg-card px-2.5 py-2">
        <div className="mb-2 h-2 w-14 rounded bg-muted animate-pulse motion-reduce:animate-none" />
        <div className="space-y-1.5">
          <div className="h-2.5 w-full rounded bg-muted animate-pulse motion-reduce:animate-none" />
          <div className="h-2.5 w-[92%] rounded bg-muted animate-pulse motion-reduce:animate-none" />
          <div className="h-2.5 w-[68%] rounded bg-muted animate-pulse motion-reduce:animate-none" />
        </div>
        <span className="sr-only">{composingSr}</span>
      </div>
    </div>
  );
}

/** Shown after the fast answer has landed while the deeper synthesis runs —
 *  the card + curated answer are already on screen; this just signals the
 *  prose is being refined, so the wait reads as "sharpening", not "hung". */
function DeepeningHint() {
  const refiningLabel = useAskLabel('refining');
  return (
    <div className={cn(tokenText.muted, 'flex items-center gap-1.5 pl-7')} role="status">
      <Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" />
      {refiningLabel}
    </div>
  );
}

function LaneBlock({
  children,
  icon,
  label,
}: {
  children: React.ReactNode;
  icon?: React.ReactNode;
  label?: string;
}) {
  return (
    <div className="flex justify-start gap-1.5">
      <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-primary/10 text-primary">
        {icon ?? <Sparkle className="h-3 w-3" />}
      </div>
      <div className="max-w-[90%] rounded-lg rounded-tl-sm border border-border bg-card px-2.5 py-1.5">
        {label && (
          <div className={cn(tokenText.eyebrow, 'mb-0.5')}>{label}</div>
        )}
        {children}
      </div>
    </div>
  );
}
